import type { BackupVault } from '@repo/api-gw-sdk';
import {
  BackupStatus,
  ConditionOperatorEnum,
  ResourceType,
} from '@repo/api-gw-sdk';

import {
  backupStatus,
  resourceId,
  resourceType,
  sourceRegion,
} from '@/components/queryBuilder/properties';
import { ResourcesSelector } from '@/components/resourcesSelection/resourcesSelector';
import { useDAL } from '@/contexts/useDAL';
import { createCombineCondition } from '@/types/advanceFilter';

export const ResourcesSelectorWrapper = ({
  vault,
  initialResourcesIds,
  selectedResourcesIds,
  addResource,
  removeResource,
  disabledDeletion,
}: {
  vault: BackupVault | undefined;
  initialResourcesIds: string[];
  selectedResourcesIds: string[];
  addResource: (providerResourceId: string) => void;
  removeResource: (providerResourceId: string) => void;
  disabledDeletion?: boolean;
}) => {
  if (!vault) {
    return (
      <ResourcesSelector
        freeTextAdd={false}
        disabled
        disabledDeletion={true}
        allResources={[]}
        addResource={addResource}
        description=''
        label=''
        resources={[]}
        removeResource={removeResource}
      />
    );
  }

  return (
    <ResourcesSelectorWithVault
      vault={vault}
      initialResourcesIds={initialResourcesIds}
      selectedResourcesIds={selectedResourcesIds}
      addResource={addResource}
      removeResource={removeResource}
      disabledDeletion={disabledDeletion}
    />
  );
};

const ResourcesSelectorWithVault = ({
  vault,
  initialResourcesIds,
  selectedResourcesIds,
  addResource,
  removeResource,
  disabledDeletion,
}: {
  vault: BackupVault;
  initialResourcesIds: string[];
  selectedResourcesIds: string[];
  addResource: (providerResourceId: string) => void;
  removeResource: (providerResourceId: string) => void;
  disabledDeletion?: boolean;
}) => {
  const dal = useDAL();
  const { data: resourcesData } = dal.inventory.listResourceIdentifiers(
    createCombineCondition([
      {
        property: resourceType.name,
        operator: ConditionOperatorEnum.In,
        value: [ResourceType.AwsS3],
      },
      {
        property: sourceRegion.name,
        operator: ConditionOperatorEnum.In,
        value: [vault.region],
      },
      {
        property: backupStatus.name,
        operator: ConditionOperatorEnum.NotIn,
        value: [BackupStatus.Dlsg],
      },
    ])
  );

  const { data: initialResourcesData } = dal.inventory.listResourceIdentifiers(
    createCombineCondition([
      {
        property: resourceId.name,
        operator: ConditionOperatorEnum.In,
        value: initialResourcesIds,
      },
    ])
  );

  const allResources = (resourcesData?.resources ?? []).concat(
    initialResourcesData?.resources ?? []
  );

  return (
    <ResourcesSelector
      freeTextAdd={false}
      disabledDeletion={disabledDeletion}
      initialResourcesIds={initialResourcesIds}
      allResources={allResources}
      addResource={addResource}
      description=''
      label=''
      resources={selectedResourcesIds}
      removeResource={removeResource}
    />
  );
};
